import { EOrderExpiry, EOrderIndicate, EOrderType } from '@webtrader/core/api/order';
import { ECloseMarketOrderType, ETradeFillPolicy, ETradeFillPolicyValue } from '@webtrader/core/api/trade';
import { EDataTestIdSuffix } from '@webtrader/core/constants/test';
import { SortOrder } from '@webtrader/core/format/orderBy';
import i18n from '@webtrader/core/i18n';
import { ECommonResponseCode } from '@webtrader/core/request/message';

export const BUY_ORDER_TYPES = [EOrderType.BUY, EOrderType.BUY_STOP, EOrderType.BUY_LIMIT, EOrderType.BUY_STOP_LIMIT];
export const SELL_ORDER_TYPES = [
  EOrderType.SELL,
  EOrderType.SELL_STOP,
  EOrderType.SELL_LIMIT,
  EOrderType.SELL_STOP_LIMIT
];
export const STOP_ORDER_TYPES = [EOrderType.BUY_STOP, EOrderType.SELL_STOP];
export const LIMIT_ORDER_TYPES = [EOrderType.BUY_LIMIT, EOrderType.SELL_LIMIT];
export const STOP_LIMIT_ORDER_TYPES = [EOrderType.BUY_STOP_LIMIT, EOrderType.SELL_STOP_LIMIT];
export const MARKET_ORDER_TYPES = [EOrderType.BUY, EOrderType.SELL];
export const LIMIT_AND_STOP_ORDER_TYPES = [...STOP_ORDER_TYPES, ...LIMIT_ORDER_TYPES];

export enum EBuySellType {
  BUY,
  SELL
}

export enum EOrderTypeLabel {
  LIMIT = 'LIMIT',
  MARKET = 'MARKET',
  STOP = 'STOP',
  STOP_LIMIT = 'STOP_LIMIT'
}

export enum EOrderLimit {
  TAKE_PROFIT = 'TAKE_PROFIT',
  STOP_LOSS = 'STOP_LOSS'
}

export enum EOrderActionType {
  BUY = 'BUY',
  SELL = 'SELL'
}

export enum EOrderSortOption {
  SYMBOL = 'SYMBOL',
  PROFIT = 'PROFIT',
  OPEN_TIME = 'OPEN_TIME',
  CLOSE_TIME = 'CLOSE_TIME'
}

export enum ECloseOrderType {
  FULL = 'FULL',
  PARTIAL = 'PARTIAL'
}

export interface ISortOrderOption {
  value: EOrderSortOption;
  order: SortOrder;
}

export const defaultCloseOrderContext = {
  orderId: 0,
  orderType: 6,
  symbol: ''
};

export enum EAssetsOrderTab {
  OPEN_POSITION = 'OPEN_POSITION',
  PENDING_ORDER = 'PENDING_ORDER',
  ORDER_HISTORY = 'ORDER_HISTORY',
  POSITION_HISTORY = 'POSITION_HISTORY',
  ORDERS_AND_DEALS = 'ORDERS_AND_DEALS',
  ORDER_HISTORY_CENTROID = 'ORDER_HISTORY_CENTROID'
}

export enum EOrderTab {
  ONE_CLICK = 'one_click',
  TRADE = 'trade',
  SPECIFICATION = 'specification'
}

export enum ETradeSymbolOrdersTab {
  SPECIFICATION = 'SPECIFICATION'
}

export enum EOrderProfitStatus {
  ALL = 'ALL',
  PROFIT = 'PROFIT',
  LOSS = 'LOSS'
}

export enum EOrderExportColumn {
  COMMISSION = 'COMMISSION',
  CURRENT_PRICE = 'CURRENT_PRICE',
  ENTRY_PRICE = 'ENTRY_PRICE',
  OPEN_DATE = 'OPEN_DATE',
  ORDER_ID = 'ORDER_ID',
  PROFIT_LOSS = 'PROFIT_LOSS',
  STOP_LOSS = 'STOP_LOSS',
  SWAP = 'SWAP',
  SYMBOL = 'SYMBOL',
  TAKE_PROFIT = 'TAKE_PROFIT',
  TYPE = 'TYPE',
  UNITS = 'UNITS',
  VOLUME_SIZE = 'VOLUME_SIZE'
}

export enum EOrderExportDealHistoriesColumn {
  CLOSE_DATE = 'CLOSE_DATE',
  COMMISSION = 'COMMISSION',
  DEAL_TYPE = 'DEAL_TYPE',
  ENTRY_PRICE = 'ENTRY_PRICE',
  FILL_POLICY = 'FILL_POLICY',
  OPEN_DATE = 'OPEN_DATE',
  ORDER_ID = 'ORDER_ID',
  PROFIT_LOSS = 'PROFIT_LOSS',
  REMARKS = 'REMARKS',
  STATE = 'STATE',
  STOP_LOSS = 'STOP_LOSS',
  SWAP = 'SWAP',
  SYMBOL = 'SYMBOL',
  TAKE_PROFIT = 'TAKE_PROFIT',
  TYPE = 'TYPE',
  UNITS = 'UNITS',
  VOLUME_SIZE = 'VOLUME_SIZE'
}

export enum EOrderExportHistoriesColumn {
  CLOSE_DATE = 'CLOSE_DATE',
  CLOSE_PRICE = 'CLOSE_PRICE',
  COMMISSION = 'COMMISSION',
  ENTRY_PRICE = 'ENTRY_PRICE',
  OPEN_DATE = 'OPEN_DATE',
  ORDER_ID = 'ORDER_ID',
  PROFIT_LOSS = 'PROFIT_LOSS',
  REMARKS = 'REMARKS',
  STATUS = 'STATUS',
  STOP_LOSS = 'STOP_LOSS',
  SWAP = 'SWAP',
  SYMBOL = 'SYMBOL',
  TAKE_PROFIT = 'TAKE_PROFIT',
  TYPE = 'TYPE',
  UNITS = 'UNITS',
  VOLUME_SIZE = 'VOLUME_SIZE'
}

export enum EOrderExportPendingColumn {
  CURRENT_PRICE = 'CURRENT_PRICE',
  EXPIRY = 'EXPIRY',
  EXPIRY_DATE = 'EXPIRY_DATE',
  FILL_POLICY = 'FILL_POLICY',
  OPEN_TIME = 'OPEN_TIME',
  ORDER_ID = 'ORDER_ID',
  PENDING_PRICE = 'PENDING_PRICE',
  PRICE = 'PRICE',
  STOP_LOSS = 'STOP_LOSS',
  SYMBOL = 'SYMBOL',
  TAKE_PROFIT = 'TAKE_PROFIT',
  TYPE = 'TYPE',
  UNITS = 'UNITS',
  VOLUME_SIZE = 'VOLUME_SIZE'
}

export type TOrderExportColumn =
  | EOrderExportColumn
  | EOrderExportDealHistoriesColumn
  | EOrderExportHistoriesColumn
  | EOrderExportPendingColumn;

export type TAssetsOrderTab = EAssetsOrderTab | ETradeSymbolOrdersTab;

export const closeOrderOptions = [
  {
    label: () => i18n.t('Full Close'),
    value: ECloseMarketOrderType.FULL_CLOSE
  },
  {
    label: () => i18n.t('Partial Close'),
    value: ECloseMarketOrderType.PARTIAL_CLOSE
  }
];

export const expiryOptions = [
  {
    label: () => i18n.t('GTC'),
    value: EOrderExpiry.GTC
  },
  {
    label: () => i18n.t('GTD'),
    value: EOrderExpiry.GTD
  }
];

export const indicateByOptions = [
  {
    label: () => i18n.t('POINTS'),
    value: EOrderIndicate.POINTS
  },
  {
    label: () => i18n.t('PRICE'),
    value: EOrderIndicate.PRICE
  }
];

export const ORDER_TYPE_LABEL_OPTIONS = [
  {
    label: () => i18n.t('Market'),
    value: EOrderTypeLabel.MARKET,
    dataTestIdSuffix: EDataTestIdSuffix.MARKET
  },
  {
    label: () => i18n.t('Limit'),
    value: EOrderTypeLabel.LIMIT,
    dataTestIdSuffix: EDataTestIdSuffix.LIMIT
  },
  {
    label: () => i18n.t('Stop'),
    value: EOrderTypeLabel.STOP,
    dataTestIdSuffix: EDataTestIdSuffix.STOP
  },
  {
    label: () => i18n.t('Stop Limit'),
    value: EOrderTypeLabel.STOP_LIMIT,
    dataTestIdSuffix: EDataTestIdSuffix.STOP_LIMIT
  }
];

export const TRADE_FILL_POLICY_TEXT_FN_MAP: Record<ETradeFillPolicy, () => string> = {
  [ETradeFillPolicy.FOK]: () => i18n.t('Fill or Kill'),
  [ETradeFillPolicy.IOC]: () => i18n.t('Immediate or Cancel'),
  [ETradeFillPolicy.RETURN]: () => i18n.t('Return'),
  [ETradeFillPolicy.GTC]: () => i18n.t('Good Till Cancelled')
};

export const TRADE_FILL_POLICY_VALUE_TEXT_FN_MAP: Record<ETradeFillPolicyValue, () => string> = {
  [ETradeFillPolicyValue.FOK]: () => i18n.t('Fill or Kill'),
  [ETradeFillPolicyValue.IOC]: () => i18n.t('Immediate or Cancel'),
  [ETradeFillPolicyValue.RETURN]: () => i18n.t('Return'),
  [ETradeFillPolicyValue.GTC]: () => i18n.t('Good Till Cancelled'),
  [ETradeFillPolicyValue.GTD]: () => i18n.t('Good Till Day'),
  [ETradeFillPolicyValue.DAY]: () => i18n.t('Day')
};

export const BUY_SELL_TYPE_LABEL_TEXT_FN_MAP: Record<EBuySellType, () => string> = {
  [EBuySellType.BUY]: () => i18n.t('BUY'),
  [EBuySellType.SELL]: () => i18n.t('SELL')
};

export const ORDER_TYPE_EDIT_ORDER_TAG_TEXT_FN_MAP: Record<EOrderType, () => string> = {
  [EOrderType.BUY]: () => i18n.t('BUY'),
  [EOrderType.BUY_LIMIT]: () => i18n.t('BUY LIMIT'),
  [EOrderType.BUY_STOP]: () => i18n.t('BUY STOP'),
  [EOrderType.BUY_STOP_LIMIT]: () => i18n.t('BUY STOP LIMIT'),
  [EOrderType.SELL]: () => i18n.t('SELL'),
  [EOrderType.SELL_LIMIT]: () => i18n.t('SELL LIMIT'),
  [EOrderType.SELL_STOP]: () => i18n.t('SELL STOP'),
  [EOrderType.SELL_STOP_LIMIT]: () => i18n.t('SELL STOP LIMIT'),
  [EOrderType.BALANCE]: () => '',
  [EOrderType.CREDIT]: () => '',
  [EOrderType.CLOSE_BY]: () => ''
};

export const ORDERS_MAX_SIZE = 30;

const checkIsOrderSymbolGeneralError = (errorCode: ECommonResponseCode) => {
  const ORDER_SYSTEM_GENERAL_ERRORS = [
    ECommonResponseCode.A_1001,
    ECommonResponseCode.A_1004,
    ECommonResponseCode.A_1010,
    ECommonResponseCode.A_1029,
    ECommonResponseCode.A_1030,
    ECommonResponseCode.MT5_10047
  ];
  return errorCode ? ORDER_SYSTEM_GENERAL_ERRORS.includes(errorCode) : false;
};

export const getOrderErrorTitle = (errorCode: ECommonResponseCode) => {
  if (checkIsOrderSymbolGeneralError(errorCode)) {
    return i18n.t('System General Error');
  }
  return i18n.t('Invalid order');
};
